import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

export const RECIPIENT_COLORS: Record<number, { bg: string; border: string; text: string; label: string; dot: string }> = {
  1: { bg: 'bg-green-50', border: 'border-green-400', text: 'text-green-700', label: 'Green', dot: 'bg-green-500' },
  2: { bg: 'bg-yellow-50', border: 'border-yellow-400', text: 'text-yellow-700', label: 'Yellow', dot: 'bg-yellow-500' },
  3: { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', label: 'Blue', dot: 'bg-blue-500' },
  4: { bg: 'bg-pink-50', border: 'border-pink-400', text: 'text-pink-700', label: 'Pink', dot: 'bg-pink-500' },
};

export const RECIPIENT_HEX_COLORS: Record<number, string> = {
  1: '#22c55e',
  2: '#eab308',
  3: '#3b82f6',
  4: '#ec4899',
};

const MAX_RECIPIENTS = 4;

interface RecipientSelectorProps {
  recipients: number[];
  selectedRecipient: number;
  onSelectedChange: (recipientNumber: number) => void;
  onAddRecipient: () => void;
  onRemoveRecipient: (recipientNumber: number) => void;
  fieldsPerRecipient: Record<number, number>;
}

const RecipientSelector: React.FC<RecipientSelectorProps> = ({
  recipients,
  selectedRecipient,
  onSelectedChange,
  onAddRecipient,
  onRemoveRecipient,
  fieldsPerRecipient,
}) => {
  const { t } = useTranslation(['templates']);
  const { toast } = useToast();

  const handleRemove = () => {
    if (recipients.length <= 1) {
      toast({ title: t('templates:editor_cannot_remove_last'), variant: 'destructive' });
      return;
    }
    const fieldCount = fieldsPerRecipient[selectedRecipient] || 0;
    if (fieldCount > 0) {
      toast({ title: t('templates:editor_cannot_remove_has_fields'), variant: 'destructive' });
      return;
    }
    onRemoveRecipient(selectedRecipient);
  };

  const handleAdd = () => {
    if (recipients.length >= MAX_RECIPIENTS) {
      toast({ title: t('templates:editor_max_recipients'), variant: 'destructive' });
      return;
    }
    onAddRecipient();
  };

  const colors = RECIPIENT_COLORS[selectedRecipient] || RECIPIENT_COLORS[1];

  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold mb-1">{t('templates:editor_recipients_title')}</h3>
      <p className="text-xs text-muted-foreground mb-2">{t('templates:editor_recipients_description')}</p>
      <div className="flex items-center gap-2">
        <Select
          value={selectedRecipient.toString()}
          onValueChange={(val) => onSelectedChange(Number(val))}
        >
          <SelectTrigger className={`flex-1 ${colors.border} ${colors.bg}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {recipients.map((num) => {
              const rc = RECIPIENT_COLORS[num];
              return (
                <SelectItem key={num} value={num.toString()}>
                  <span className="flex items-center gap-2">
                    <span className={`inline-block w-3 h-3 rounded-full ${rc.dot}`} />
                    {t('templates:editor_recipient', { number: num })}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          onClick={handleAdd}
          disabled={recipients.length >= MAX_RECIPIENTS}
          title={t('templates:editor_add_recipient')}
        >
          <Plus className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleRemove}
          disabled={recipients.length <= 1}
          title={t('templates:editor_remove_recipient')}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default RecipientSelector;
